import FolderIcon from "@mui/icons-material/Folder";
import InboxIcon from "@mui/icons-material/Inbox";
import {
  Avatar,
  Badge,
  Box,
  List as MuiList,
  ListItemAvatar,
  ListItemButton as MuiListItemButton,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import React from "react";
import IconComponent from "../../../common/components/IconComponent";
import LoadingOverlay from "../../../common/components/LoadingOverlay";
import { useErrorHandler } from "../../../common/hooks";
import { VariableNames } from "../../Constants/VariableNames";
import BundleContext from "../../Contexts/BundleContext";
import { useDesignerMode } from "../../Contexts/DesignerModeContext";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useMaskPiiSetting from "../../Hooks/useMaskPiiSetting";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { ListItemModel, ListModel } from "../../Models/ListModel";
import { IsDesignMode } from "../../Utils/runtimeDetection";
import IComponentProps from "../IComponentProps";
import { coerceListItems, getListFieldValue, renderListFieldValue } from "../ListShared/listItemDisplay";
import ListPanelHeader from "../ListShared/ListPanelHeader";
import { useSelectableListItems } from "../ListShared/useSelectableListItems";
import PanelAction from "../Panel/PanelAction";
import getComponentLayout from "../Table/getComponentLayout";

const LIST_ITEMS_PATH_SEGMENT = "items";
const LIST_ITEM_TYPE = "ListItemModel";

function getItemTemplate(component: ListModel): ListItemModel {
  return component.Items?.[0] ?? component;
}

type ListRowButtonProps = {
  component: ListModel;
  context: IComponentProps<ListModel>["context"];
  componentPath: string[];
  item: any;
  itemKey: string | number;
  index: number;
  selected: boolean;
  primary: React.ReactNode;
  secondary: React.ReactNode;
  supporting: React.ReactNode;
  badge: React.ReactNode;
  icon: React.ReactNode;
  useSelection: boolean;
  onSelect: () => void;
};

const ListRowButton: React.FC<ListRowButtonProps> = ({
  component,
  context,
  componentPath,
  item,
  itemKey,
  index,
  selected,
  primary,
  secondary,
  supporting,
  badge,
  icon,
  useSelection,
  onSelect,
}) => {
  const selectionProps = useComponentSelection({
    component: {
      Id: `${component.Id}-item-${itemKey}`,
      Type: LIST_ITEM_TYPE,
      Layout: {},
    },
    componentPath: [...componentPath, LIST_ITEMS_PATH_SEGMENT, String(index)],
    isContainer: false,
  });

  return (
    <Box {...selectionProps} sx={{ display: "block" }}>
      <MuiListItemButton
        selected={useSelection && selected}
        onMouseDown={(event) => {
          if (useSelection && IsDesignMode && (event.ctrlKey || event.metaKey)) {
            onSelect();
          }
        }}
        onClick={useSelection ? onSelect : undefined}
        sx={{ alignItems: "center", gap: 1 }}
      >
        <ListItemAvatar sx={{ minWidth: 40, display: "flex", alignItems: "center" }}>
          <Badge badgeContent={badge} color="primary">
            <Avatar sx={{ width: 32, height: 32 }}>{icon}</Avatar>
          </Badge>
        </ListItemAvatar>
        <ListItemText
          primary={primary ?? ""}
          secondary={
            <>
              {secondary !== undefined && <span>{secondary}</span>}
              {supporting !== undefined && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {supporting}
                </Typography>
              )}
            </>
          }
        />
        {!!component.ItemActions?.length && (
          <Box
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, ml: 1 }}
          >
            <PanelAction
              actions={component.ItemActions}
              size="small"
              formatData={item}
              context={context}
              ownerComponentId={component.Id}
              ownerComponentPath={componentPath}
              actionPathSegment="itemActions"
            />
          </Box>
        )}
      </MuiListItemButton>
    </Box>
  );
};

const ListComponent: React.FC<IComponentProps<ListModel>> = ({ component, context, componentPath = [] }) => {
  const errorHandler = useErrorHandler();
  const designerMode = useDesignerMode();
  const bundleContext = React.useContext(BundleContext);
  const [maskPii] = useMaskPiiSetting();
  const currencyCode = bundleContext.Settings?.CurrencyCode;
  const phoneNumberFormat = bundleContext.Settings?.PhoneNumberFormat;
  const componentLayoutSx = getComponentLayout(component);
  const keyField = component.KeyField ?? "Id";
  const persistedKey = component.PersistedStateKey ?? `list.${component.Id}`;
  const [items, itemsPending, refreshItems] = useObservedExpression<any[]>(component.GetItems, context, "ListComponent.GetItems", (value) =>
    coerceListItems(value, "List GetItems")
  );
  const [currentItem] = useObservedExpression<any>(component.Item, context, "ListComponent.Item");
  const useSelection = component.UseSelection !== false;
  const handleItemSelected = React.useCallback(
    async (item: any) => {
      if (component.OnItemSelected) {
        const eventContext = context.CreateChild();
        eventContext.VariableScope.set(VariableNames.Item, item);
        eventContext.VariableScope.set(VariableNames.Event, item);
        await eventContext.Evaluator.evaluate(component.OnItemSelected, eventContext);
      }
    },
    [component.OnItemSelected, context]
  );
  const { selectedKey, selectedItem, selectItem, getItemKey } = useSelectableListItems({
    componentId: component.Id,
    componentPath,
    itemPathSegment: LIST_ITEMS_PATH_SEGMENT,
    items,
    itemsPending,
    keyField,
    persistedStateKey: persistedKey,
    persistSelection: component.PersistSelection,
    itemQueryKey: component.ItemQueryKey,
    selectedPath: designerMode?.selectedPath,
    itemExpression: component.Item,
    currentItem,
    context,
    enabled: useSelection,
    onSelected: handleItemSelected,
    onError: errorHandler,
  });

  const selectionProps = useComponentSelection({
    component,
    componentPath,
    isContainer: false,
  });
  const designModeMinHeight = IsDesignMode ? componentLayoutSx.minHeight ?? 80 : componentLayoutSx.minHeight;
  const hasItems = (items?.length ?? 0) > 0;
  const itemTemplate = getItemTemplate(component);
  const headerTitle = component.HeaderTitle ?? component.Title;
  const useRefreshAction = component.UseRefreshAction !== false;
  const header = (
    <ListPanelHeader
      title={headerTitle}
      iconName={component.HeaderIcon}
      showRefresh={useRefreshAction}
      isRefreshing={itemsPending}
      onRefresh={() => refreshItems()}
      actions={component.HeaderActions}
      actionData={selectedItem}
      context={context}
      ownerComponentId={component.Id}
      ownerComponentPath={componentPath}
    />
  );

  const content = (
    <>
      <LoadingOverlay loading={itemsPending} />
      {header}
      {!itemsPending && !hasItems && (
        <Box sx={{ minHeight: 96, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, color: "text.disabled", px: 2, py: 3 }}>
          <InboxIcon fontSize="large" />
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            No items
          </Typography>
        </Box>
      )}
      {hasItems && (
        <MuiList dense disablePadding>
          {(items ?? []).map((item, index) => {
            const key = getItemKey(item, index);
            const primaryFieldValue = getListFieldValue(item, itemTemplate.PrimaryTextField);
            const primary =
              renderListFieldValue(primaryFieldValue, itemTemplate.PrimaryTextField, "Primary Text", itemTemplate.PrimaryTextFormat, currencyCode, phoneNumberFormat, maskPii) ??
              item?.Label ??
              item?.Name ??
              key;
            const secondary = renderListFieldValue(
              getListFieldValue(item, itemTemplate.SecondaryTextField),
              itemTemplate.SecondaryTextField,
              "Secondary Text",
              itemTemplate.SecondaryTextFormat,
              currencyCode,
              phoneNumberFormat,
              maskPii
            );
            const supporting = renderListFieldValue(
              getListFieldValue(item, itemTemplate.SupportingTextField),
              itemTemplate.SupportingTextField,
              "Supporting Text",
              itemTemplate.SupportingTextFormat,
              currencyCode,
              phoneNumberFormat,
              maskPii
            );
            const badge = renderListFieldValue(
              getListFieldValue(item, itemTemplate.BadgeField),
              itemTemplate.BadgeField,
              "Badge",
              itemTemplate.BadgeFormat,
              currencyCode,
              phoneNumberFormat,
              maskPii
            );
            const icon = itemTemplate.IconName ? <IconComponent iconName={itemTemplate.IconName} /> : <FolderIcon />;

            return (
              <ListRowButton
                key={key}
                component={component}
                context={context}
                componentPath={componentPath}
                item={item}
                itemKey={key}
                index={index}
                selected={selectedKey !== null && String(selectedKey) === String(key)}
                primary={primary}
                secondary={secondary}
                supporting={supporting}
                badge={badge}
                icon={icon}
                useSelection={useSelection}
                onSelect={() => selectItem(item, index)}
              />
            );
          })}
        </MuiList>
      )}
    </>
  );
  const surfaceSx = {
    m: 1,
    ...componentLayoutSx,
    minHeight: designModeMinHeight,
    position: "relative",
    overflow: "hidden",
  } as const;

  if (component.UsePaper === false) {
    return (
      <Box sx={surfaceSx} {...selectionProps}>
        {content}
      </Box>
    );
  }

  return (
    <Paper elevation={1} sx={surfaceSx} {...selectionProps}>
      {content}
    </Paper>
  );
};

export default ListComponent;
